function adjustLightness(
  amount: number,
  color: string,
  type: 'lighten' | 'darken',
) {
  let r, g, b, a;

  if (color.startsWith('#')) {
    if (color.length === 4) {
      color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    // Hex to RGB
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
    a = 1; // Default alpha for hex
  } else if (color.startsWith('rgba')) {
    // RGBA to RGB
    const rgbaValues = color.substring(5, color.length - 1).split(',');
    r = parseInt(rgbaValues[0].trim());
    g = parseInt(rgbaValues[1].trim());
    b = parseInt(rgbaValues[2].trim());
    a = parseFloat(rgbaValues[3].trim());
  } else {
    // Invalid color format
    return color;
  }

  // RGB to HSL
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  // Adjust lightness
  if (type === 'lighten') {
    l += amount;
  } else if (type === 'darken') {
    l -= amount;
  }

  l = Math.max(0, Math.min(1, l));

  // HSL to RGB
  let rNew, gNew, bNew;

  if (s === 0) {
    rNew = gNew = bNew = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rNew = hue2rgb(p, q, h + 1 / 3);
    gNew = hue2rgb(p, q, h);
    bNew = hue2rgb(p, q, h - 1 / 3);
  }

  // RGB to hex
  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  if (a !== 1) {
    return `rgba(${Math.round(rNew * 255)}, ${Math.round(
      gNew * 255,
    )}, ${Math.round(bNew * 255)}, ${a})`;
  }

  return `#${toHex(rNew)}${toHex(gNew)}${toHex(bNew)}`;
}

export function lighten(amount: number, color: string) {
  return adjustLightness(amount, color, 'lighten');
}

export function darken(amount: number, color: string) {
  return adjustLightness(amount, color, 'darken');
}

export function transparentize(amount: number, color: string) {
  let r, g, b, a;

  if (color.startsWith('#')) {
    if (color.length === 4) {
      color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    // Hex to RGBA
    r = parseInt(color.slice(1, 3), 16);
    g = parseInt(color.slice(3, 5), 16);
    b = parseInt(color.slice(5, 7), 16);
    a = 1; // Default alpha for hex
  } else if (color.startsWith('rgba')) {
    // RGBA to RGBA
    const rgbaValues = color.substring(5, color.length - 1).split(',');
    r = parseInt(rgbaValues[0].trim());
    g = parseInt(rgbaValues[1].trim());
    b = parseInt(rgbaValues[2].trim());
    a = parseFloat(rgbaValues[3].trim());
  } else {
    // Invalid color format
    return color;
  }

  // Adjust alpha
  a -= amount;
  a = Math.max(0, Math.min(1, a));
  a = Math.round(a * 100) / 100;

  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
